import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createPartySupMast,
  fetchAllPartySupMasts,
  updatePartySupMast,
  fetchOnePartySupMast,
  deletePartySupMast,
  createGroup,
  updateGroup,
  deleteGroup,
  createGeneralAccount,
  updateGeneralAccount,
  deleteGeneralAccount,
} from "services/partysupmast";

export const useAllPartySupMasts = (filters = {}, options = {}) => {
  const query = useQuery({
    queryKey: ["all-partysupmasts", filters],
    queryFn: () => {
      return fetchAllPartySupMasts(filters);
    },
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    ...options,
  });
  return query;
};

export const useOnePartySupMast = (id) => {
  const query = useQuery({
    queryKey: ["one-partysupmast", id],
    queryFn: () => {
      if (id) {
        return fetchOnePartySupMast(id);
      }
    },
  });
  return query;
};

export const useCreatePartySupMast = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => createPartySupMast(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
    },
  });
};

export const useUpdatePartySupMast = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => updatePartySupMast(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
      queryClient.invalidateQueries({ queryKey: ["one-partysupmast", variables.id] });
    },
  });
};

export const useDeletePartySupMast = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => deletePartySupMast(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
      queryClient.invalidateQueries({ queryKey: ["one-partysupmast", id] });
    },
  });
};



// Group hooks (groups are partysupmast records with level=1)
export const useCreateGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => createGroup(data),
    onSuccess: () => {
      // Only invalidate queries here, no callbacks
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
    },
  });
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateGroup(data, id),
    onSuccess: (data, variables) => {
      // Only invalidate queries here, no callbacks
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
      queryClient.invalidateQueries({ queryKey: ["one-partysupmast", variables.id] });
    },
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => deleteGroup(id),
    onSuccess: (data, id) => {
      // Only invalidate queries here, no callbacks
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
      queryClient.invalidateQueries({ queryKey: ["one-partysupmast", id] });
    },
  });
};

// General Account hooks - simplified to use existing hook

export const useCreateGeneralAccount = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => createGeneralAccount(data),
    onSuccess: (data) => {
      // Invalidate all partysupmast queries (covers all filters)
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
    },
  });
};

export const useUpdateGeneralAccount = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateGeneralAccount(id, data),
    onSuccess: (data, variables) => {
      // Invalidate all partysupmast queries (covers all filters)
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
      queryClient.invalidateQueries({ queryKey: ["one-partysupmast", variables.id] });
    },
  });
};

export const useDeleteGeneralAccount = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => deleteGeneralAccount(id),
    onSuccess: (data, id) => {
      // Invalidate all partysupmast queries (covers all filters)
      queryClient.invalidateQueries({ queryKey: ["all-partysupmasts"] });
      queryClient.invalidateQueries({ queryKey: ["one-partysupmast", id] });
    },
  });
};
